import * as React from "react";
import { Box, Chip, Typography, Stack } from "@mui/material";

interface CapabilityGridProps {
  /** Maps provider id -> the exact capability strings the backend reports for it. */
  capabilitiesByProvider: Record<string, string[]>;
}

/**
 * Renders whichever capability strings a provider genuinely advertises
 * (`GET /brokers/capabilities`) - never a fixed checklist. The backend's
 * closed `BrokerCapability` vocabulary (market_data/historical_data/
 * order_placement/order_modification/order_cancellation/funds/positions/
 * holdings/margin) does not match this sprint brief's suggested labels
 * (Paper Trading/Execution/Synchronization), so this component displays
 * the real, backend-reported capability names rather than fabricating
 * checkmarks for concepts the provider does not actually report.
 */
export function CapabilityGrid({ capabilitiesByProvider }: CapabilityGridProps): React.JSX.Element {
  const providerIds = Object.keys(capabilitiesByProvider);

  if (providerIds.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No capabilities reported by the backend.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {providerIds.map((providerId) => (
        <Box key={providerId}>
          <Typography variant="subtitle2" gutterBottom>
            {providerId}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {capabilitiesByProvider[providerId].length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No capabilities reported for this provider.
              </Typography>
            ) : (
              capabilitiesByProvider[providerId].map((capability) => (
                <Chip key={capability} size="small" label={capability} variant="outlined" />
              ))
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
